import { DestroyRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

/** The URL without its `#fragment`. A serialized URL has no other `#`. */
function withoutFragment(url: string): string {
  return url.split('#')[0];
}

/**
 * True when only the `#fragment` changed: the same page, the same query, a
 * different fragment. A tab strip, a drawer or a deep link to a section of the
 * page says where it is this way; it is a state of the page, not a new one.
 */
export function isFragmentOnlyChange(previous: string, next: string): boolean {
  return previous !== next && withoutFragment(previous) === withoutFragment(next);
}

/**
 * Move focus to <main> after each navigation.
 *
 * A single-page application does not reload, so focus stays wherever it was
 * — usually on a link in the header that has just been replaced. The user is
 * left with focus on stale content and no indication the page changed. Moving
 * focus to <main id="body-container" tabindex="-1"> gives them the same
 * "you are somewhere new, start reading here" cue a full page load provides,
 * and pairs with the title change and announcement in CpdTitleStrategy
 * (WCAG 2.4.3, 4.1.3).
 *
 * A fragment-only navigation is not a new page, so focus is left where the
 * page put it: the next tab after an arrow key, the panel heading after a tab
 * click, an opened credential drawer, a deep-linked occupation section. Moving
 * it to <main> one tick later threw all of those away.
 *
 * preventScroll because scrollPositionRestoration already owns scrolling; and
 * the first load is skipped so we do not fight the browser's own focus.
 */
export function manageRouteFocus(router: Router, destroyRef: DestroyRef): void {
  let previous: string | null = null;
  router.events
    .pipe(
      takeUntilDestroyed(destroyRef),
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    )
    .subscribe((event) => {
      const last = previous;
      previous = event.urlAfterRedirects;
      if (last === null || isFragmentOnlyChange(last, event.urlAfterRedirects)) {
        return;
      }
      // NavigationEnd can fire before the routed view has rendered, so the
      // target may not exist yet. Defer a tick and re-query.
      setTimeout(() => {
        document.getElementById('body-container')?.focus({ preventScroll: true });
      });
    });
}
